/**
 * @fileoverview Outreach Vuex actions for managing contacts, tags, stages, and metrics.
 * @module store/modules/outreach/actions
 */

import { graph } from "@/store/api";
import { RootState } from "@/store/types";
import { gql } from "@apollo/client/core";
import { ActionTree } from "vuex";
import { OutreachState, OUT_REACH_TAG_TYPE } from "./types";

const actions: ActionTree<OutreachState, RootState> = {
  /**
   * Fetches outreach contacts with pagination and filtering.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {number} [payload.take=10] - Number of contacts to fetch
   * @param {number} [payload.page=1] - Page number
   * @param {string} [payload.startDate=''] - Start date filter
   * @param {string} [payload.endDate=''] - End date filter
   * @param {Array} [payload.filter] - Filter criteria
   * @param {Object} [payload.sort] - Sort configuration
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('FETCH_OUTREACH_CONTACTS', {
   *   take: 20,
   *   page: 1,
   *   filter: [{ field: 'country', value: 'Singapore' }]
   * });
   */
  FETCH_OUTREACH_CONTACTS: async (
    { commit },
    { take = 10, page = 1, startDate = "", endDate = "", filter, sort }
  ) => {
    commit("SET_OUTREACH_LOADING", true);
    return graph
      .query({
        query: gql`
          query (
            $take: Int!
            $page: Int!
            $startDate: String
            $endDate: String
            $filter: [JSON]
            $sort: JSON
          ) {
            outReachContactAPI {
              getAllOutReachContacts(
                take: $take
                page: $page
                startDate: $startDate
                endDate: $endDate
                filter: $filter
                sort: $sort
              )
            }
          }
        `,
        variables: {
          take,
          page,
          startDate,
          endDate,
          filter,
          sort,
        },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        const result = data.outReachContactAPI.getAllOutReachContacts;
        commit("SET_OUTREACH_CONTACTS", result);
        commit("SET_OUTREACH_LOADING", false);
      });
  },
  EXPORT_OUTREACH_CONTACTS: async (
    { commit },
    { take = 500, startDate = "", endDate = "", filter, sort } = {}
  ) => {
    commit("SET_OUTREACH_LOADING", true);

    try {
      let page = 1;
      let total = 0;
      let allContacts: any[] = [];
      let hasMoreContacts = true;

      while (hasMoreContacts) {
        const { data } = await graph.query({
          query: gql`
            query (
              $take: Int!
              $page: Int!
              $startDate: String
              $endDate: String
              $filter: [JSON]
              $sort: JSON
            ) {
              outReachContactAPI {
                getAllOutReachContacts(
                  take: $take
                  page: $page
                  startDate: $startDate
                  endDate: $endDate
                  filter: $filter
                  sort: $sort
                )
              }
            }
          `,
          variables: {
            take,
            page,
            startDate,
            endDate,
            filter,
            sort,
          },
          fetchPolicy: "network-only",
        });

        const result = data.outReachContactAPI.getAllOutReachContacts;
        const contacts = result.data || [];
        total = Number(result.count || 0);
        allContacts = allContacts.concat(contacts);

        hasMoreContacts = contacts.length > 0 && allContacts.length < total;
        page += 1;
      }

      return {
        count: total,
        data: allContacts,
      };
    } finally {
      commit("SET_OUTREACH_LOADING", false);
    }
  },
  /**
   * Fetches the count of outreach contacts matching filters.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {number} [payload.take=10] - Number of contacts per page
   * @param {number} [payload.page=1] - Page number
   * @param {Array} [payload.filter] - Filter criteria
   * @returns {Promise<number>} Total count of contacts
   */
  FETCH_COUNT_OUTREACH_CONTACT: async ({ commit }, { take = 10, page = 1, filter }) => {
    const { data } = await graph.query({
      query: gql`
        query ($take: Int!, $page: Int!, $filter: [JSON]) {
          outReachContactAPI {
            getAllOutReachContacts(take: $take, page: $page, filter: $filter)
          }
        }
      `,
      variables: {
        take,
        page,
        filter,
      },
      fetchPolicy: "network-only",
    });
    return data.outReachContactAPI.getAllOutReachContacts.count;
  },
  /**
   * Fetches outreach segments (tags and stages) from backend.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('FETCH_OUTREACH_SEGMENTS');
   */
  FETCH_OUTREACH_SEGMENTS: async ({ commit }) => {
    return graph
      .query({
        query: gql`
          query {
            outReachTagStageAPI {
              getOutReachTags
            }
          }
        `,
        fetchPolicy: "network-only",
      })
      .then((res) => {
        const result = res.data.outReachTagStageAPI.getOutReachTags;
        const segmentsTags = result.filter((tag) => tag.type === OUT_REACH_TAG_TYPE.TAG);
        const segmentsStages = result.filter((tag) => tag.type === OUT_REACH_TAG_TYPE.STAGE);
        commit("SET_OUTREACH_TAGS", segmentsTags);
        commit("SET_OUTREACH_STAGES", segmentsStages);
        commit("SET_OUTREACH_LOADING", false);
      });
  },
  /**
   * Fetches all contact attributes from backend.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('FETCH_CONTACT_ATTRIBUTES');
   */
  FETCH_CONTACT_ATTRIBUTES: async ({ commit }) => {
    return graph
      .query({
        query: gql`
          query {
            outReachContactAPI {
              getAllContactAttributes
            }
          }
        `,
        fetchPolicy: "network-only",
      })
      .then((res) => {
        const result = res.data.outReachContactAPI.getAllContactAttributes;
        commit("SET_CONTACT_ATTRIBUTES", result);
        commit("SET_OUTREACH_LOADING", false);
      });
  },
  /**
   * Fetches outreach metrics with contact counts by filter groups.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('FETCH_OUTREACH_METRICS');
   */
  FETCH_OUTREACH_METRICS: async ({ commit }) => {
    const result = await graph.query({
      query: gql`
        query ($filter: [JSON]) {
          outReachContactAPI {
            countAllOutReachContacts(filter: $filter)
          }
          outReachGroupFilterAPI {
            getAllOutReachGroupFilters
          }
        }
      `,
      variables: {
        filters: [],
      },
      fetchPolicy: "network-only",
    });
    const counts = result.data.outReachContactAPI.countAllOutReachContacts.filterResult;
    const metrics = result.data.outReachGroupFilterAPI.getAllOutReachGroupFilters.map((item, i) => ({
      id: item.id,
      name: item.groupName,
      color: item.colorValue,
      filterList: JSON.parse(item.filters),
      value: counts[i].filter,
    }));
    commit("SET_OUTREACH_METRICS", metrics);
  },
  /**
   * Adds a new outreach metric (filter group).
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} context.state - Current module state
   * @param {Object} newMetric - New metric configuration
   * @param {string} newMetric.name - Metric name
   * @param {string} newMetric.color - Display color
   * @param {Object} newMetric.filterList - Filter criteria
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('ADD_OUTREACH_METRIC', {
   *   name: 'Active Patients',
   *   color: 'blue',
   *   filterList: { 0: { variable: 'Stage', comparison: 'is', value: 'Active' } }
   * });
   */
  ADD_OUTREACH_METRIC: async ({ commit, state }, newMetric) => {
    const groupFilters = [...Object.values(state.metrics), newMetric].map(item =>  ({
      groupName: item.name,
      colorValue: item.color,
      filters: JSON.stringify(Object.values(item.filterList)),
    }));
    await graph.mutate({
      mutation: gql`
        mutation ($groupFilters: [JSON]) {
          outReachGroupFilterAPI {
            outReachCreateGroupFilter(groupFilters: $groupFilters)
          }
        }
      `,
      variables: {
        groupFilters,
      }
    });
    const result = await graph.query({
      query: gql`
        query ($filter: [JSON]) {
          outReachContactAPI {
            countAllOutReachContacts(filter: $filter)
          }
          outReachGroupFilterAPI {
            getAllOutReachGroupFilters
          }
        }
      `,
      variables: {
        filters: [],
      },
      fetchPolicy: 'no-cache',
    });
    const counts = result.data.outReachContactAPI.countAllOutReachContacts.filterResult;
    const metrics = result.data.outReachGroupFilterAPI.getAllOutReachGroupFilters.map((item, i) => ({
      id: item.id,
      name: item.groupName,
      color: item.colorValue,
      filterList: JSON.parse(item.filters),
      value: counts[i].filter,
    }));
    commit("SET_OUTREACH_METRICS", metrics);
  },
  /**
   * Edits an existing outreach metric.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} context.state - Current module state
   * @param {Object} editedMetric - Updated metric configuration
   * @param {number} editedMetric.id - Metric ID
   * @param {string} [editedMetric.name] - Updated name
   * @param {string} [editedMetric.color] - Updated color
   * @param {Object} [editedMetric.filterList] - Updated filters
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('EDIT_OUTREACH_METRIC', {
   *   id: 1,
   *   name: 'Updated Name',
   *   color: 'green'
   * });
   */
  EDIT_OUTREACH_METRIC: async ({ commit, state }, editedMetric) => {
    const updatedMetrics = { ...state.metrics, [editedMetric.id]: editedMetric };
    const groupFilters = Object.values(updatedMetrics).map((item: any) =>({
      id: item.id,
      groupName: item.name,
      colorValue: item.color,
      filters: JSON.stringify(Object.values(item.filterList)),
    }));
    await graph.mutate({
      mutation: gql`
        mutation ($groupFilters: [JSON]) {
          outReachGroupFilterAPI {
            outReachCreateGroupFilter(groupFilters: $groupFilters)
          }
        }
      `,
      variables: {
        groupFilters,
      }
    });
    const result = await graph.query({
      query: gql`
        query ($filter: [JSON]) {
          outReachContactAPI {
            countAllOutReachContacts(filter: $filter)
          }
          outReachGroupFilterAPI {
            getAllOutReachGroupFilters
          }
        }
      `,
      variables: {
        filters: [],
      },
      fetchPolicy: 'no-cache',
    });
    const counts = result.data.outReachContactAPI.countAllOutReachContacts.filterResult;
    const metrics = result.data.outReachGroupFilterAPI.getAllOutReachGroupFilters.map((item, i) => ({
      id: item.id,
      name: item.groupName,
      color: item.colorValue,
      filterList: JSON.parse(item.filters),
      value: counts[i].filter,
    }));
    commit("SET_OUTREACH_METRICS", metrics);
  },
  /**
   * Deletes an outreach metric by ID.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} context.state - Current module state
   * @param {number} metricId - ID of metric to delete
   * @returns {Promise<void>}
   *
   * @example
   * await store.dispatch('DELETE_OUTREACH_METRIC', 1);
   */
  DELETE_OUTREACH_METRIC: async ({ commit, state }, metricId) => {
    const groupFilters = Object.values(state.metrics).filter(metric => metric.id !== metricId).map((item: any) =>({
      groupName: item.name,
      colorValue: item.color,
      filters: JSON.stringify(Object.values(item.filterList)),
    }));
    await graph.mutate({
      mutation: gql`
        mutation ($groupFilters: [JSON]) {
          outReachGroupFilterAPI {
            outReachCreateGroupFilter(groupFilters: $groupFilters)
          }
        }
      `,
      variables: {
        groupFilters,
      }
    });
    const result = await graph.query({
      query: gql`
        query ($filter: [JSON]) {
          outReachContactAPI {
            countAllOutReachContacts(filter: $filter)
          }
          outReachGroupFilterAPI {
            getAllOutReachGroupFilters
          }
        }
      `,
      variables: {
        filters: [],
      },
      fetchPolicy: 'no-cache',
    });
    const counts = result.data.outReachContactAPI.countAllOutReachContacts.filterResult;
    const metrics = result.data.outReachGroupFilterAPI.getAllOutReachGroupFilters.map((item, i) => ({
      id: item.id,
      name: item.groupName,
      color: item.colorValue,
      filterList: JSON.parse(item.filters),
      value: counts[i].filter,
    }));
    commit("SET_OUTREACH_METRICS", metrics);
  }
};

export default actions;
